import { useContext } from "react";
import { Link } from "react-router-dom";
import { Link as MuiLink, Paper } from "@mui/material";
import buildUserName from "core/utils/build_user_name";
import { styled } from "@mui/material/styles";
import { CaseAction } from "interfaces/case-action";
import { AttachmentList } from "components/AttachmentList";
import { FeaturesContext } from "contexts/FeaturesContext";
import { EntitlementContext } from "contexts/UserProfileContext";
import { DateWithTooltip } from "components/DateWithTooltip";
import { useActionAttachmentsQuery } from "hooks/queries/attachments";

interface ActionInfoProps {
  selectedAction: CaseAction | undefined;
}

export const ActionInfo = ({
  selectedAction
}: ActionInfoProps): JSX.Element => {
  const {
    data: attachments,
    isLoading: isLoadingAttachments,
    isError: isErrorAttachments,
    refetch: refetchAttachments
  } = useActionAttachmentsQuery({
    actionId: selectedAction?.id
  });
  const { hasFeature } = useContext(FeaturesContext);
  const { hasEntitlement } = useContext(EntitlementContext);

  const caseManagementEnabled =
    hasFeature("case_management") && hasEntitlement("cm-view");

  return (
    <Wrapper>
      <ActionInfoFields>
        <Content>
          <DetailTitle>Case ID: &nbsp;</DetailTitle>
          {caseManagementEnabled ? (
            <MuiLink
              component={Link}
              to={`/pages/case-management/${selectedAction?.case ?? ""}`}
            >
              {selectedAction?.case_display_id ?? <>&mdash;</>}
            </MuiLink>
          ) : (
            <div>{selectedAction?.case_display_id ?? <>&mdash;</>}</div>
          )}
        </Content>
        <Content>
          <DetailTitle>Requested by: &nbsp;</DetailTitle>
          {selectedAction?.create_user_detail
            ? buildUserName(selectedAction.create_user_detail)
            : "-"}
        </Content>
        <Content>
          <DetailTitle>Type of Action: &nbsp;</DetailTitle>
          {selectedAction?.action_type_detail?.name ?? <>&mdash;</>}
        </Content>
        <Content>
          <DetailTitle>Site: &nbsp;</DetailTitle>
          {selectedAction?.site ?? <>&mdash;</>}
        </Content>
        <Content>
          <DetailTitle>Machine: &nbsp;</DetailTitle>
          {selectedAction?.machine ?? <>&mdash;</>}
        </Content>
        <Content>
          <DetailTitle>Component: &nbsp;</DetailTitle>
          {selectedAction?.component ?? <>&mdash;</>}
        </Content>
        <Content>
          <DetailTitle>Due Date: &nbsp;</DetailTitle>
          {selectedAction?.requested_completion_date ? (
            <DateWithTooltip
              date={selectedAction.requested_completion_date}
              showTime={false}
            />
          ) : (
            "Not set"
          )}
        </Content>

        <DescriptionVerticalWrapper>
          <DetailTitle>Description:</DetailTitle>
          <DescriptionText>
            {selectedAction?.description ?? <>&mdash;</>}
          </DescriptionText>
        </DescriptionVerticalWrapper>
      </ActionInfoFields>

      {selectedAction && (
        <ActionInfoAttachements>
          <DetailTitle>Attachments:</DetailTitle>
          {selectedAction &&
          !isLoadingAttachments &&
          !isErrorAttachments &&
          attachments?.length > 0 ? (
            <AttachmentList
              attachments={attachments}
              refetchFn={refetchAttachments}
            />
          ) : (
            <NoAttachmentsBox>
              <DetailTitle>No Attachments</DetailTitle>
            </NoAttachmentsBox>
          )}
        </ActionInfoAttachements>
      )}
    </Wrapper>
  );
};

const Wrapper = styled("div")`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  @media only screen and (max-width: 1280px) {
    flex-direction: column;
  }
`;

const ActionInfoFields = styled("div")`
  min-width: 50%;
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: 1280px) {
    width: 100%;
  }
`;

const ActionInfoAttachements = styled("div")`
  display: flex;
  flex-direction: column;
  min-width: 50%;
  padding: 0 ${props => props.theme.spacing(1)};
  ${props => props.theme.breakpoints.up("sm")} {
    width: 100%;
    padding: 0;
  }
`;

const NoAttachmentsBox = styled(Paper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  min-height: 50px;
`;

const Content = styled("div")`
  display: flex;
  width: 100%;
`;

const DescriptionVerticalWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${props => props.theme.spacing(1)};
`;

const DetailTitle = styled("div")`
  font-weight: bold;
  width: 150px;
`;
DetailTitle.displayName = "DetailTitle";

const DescriptionText = styled("div")`
  white-space: pre-wrap;
`;
